import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, Plus, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { inputCls, btnPrimary, Skeleton } from "@/components/portal/ui";
import { AdminTitle } from "@/pages/admin/AdminLayout";

function RoleCard({ role, areas, onSaved, onDeleted }) {
  const [menus, setMenus] = useState(new Set(role.menus));
  const [label, setLabel] = useState(role.label);
  const [busy, setBusy] = useState(false);
  const locked = role.locked;

  const toggle = (key) => {
    if (locked) return;
    const next = new Set(menus);
    next.has(key) ? next.delete(key) : next.add(key);
    setMenus(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/roles/${role.key}`, { label, menus: [...menus] });
      onSaved(data);
      toast.success(`Role "${data.label}" disimpan`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Hapus role "${role.label}"? Pastikan tidak ada pengguna yang masih memakainya.`)) return;
    try {
      await api.delete(`/admin/roles/${role.key}`);
      toast.success("Role dihapus");
      onDeleted(role.key);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid={`role-card-${role.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <input disabled={locked} className={`font-extrabold text-slate-900 bg-transparent border-none p-0 text-lg w-full ${locked ? "" : "focus:ring-0 focus:outline-none hover:bg-slate-50 rounded px-1 -mx-1"}`}
            value={label} onChange={(e) => setLabel(e.target.value)} data-testid={`role-label-${role.key}`} />
          <p className="text-xs text-slate-400 font-mono mt-0.5">{role.key}</p>
        </div>
        {locked ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 bg-amber-50 rounded-full px-2.5 py-1" data-testid={`role-locked-${role.key}`}><Lock className="w-3 h-3" />Akses Penuh</span>
        ) : !role.builtin && (
          <button onClick={remove} data-testid={`role-delete-${role.key}`} className="w-8 h-8 rounded-lg bg-red-50 text-red-600 grid place-items-center hover:bg-red-100 transition shrink-0"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>
      {locked ? (
        <p className="mt-4 text-sm text-slate-500">Role ini selalu memiliki akses ke seluruh menu demi mencegah kehilangan akses admin.</p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {areas.map((a) => (
              <label key={a.key} className="flex items-center gap-2 text-sm font-semibold text-slate-600 cursor-pointer" data-testid={`role-menu-${role.key}-${a.key}`}>
                <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={menus.has(a.key)} onChange={() => toggle(a.key)} />
                {a.label}
              </label>
            ))}
          </div>
          <button onClick={save} disabled={busy} className={`${btnPrimary} w-full !py-2.5 mt-5 text-sm`} data-testid={`role-save-${role.key}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Role"}</button>
        </>
      )}
    </div>
  );
}

function NewRoleForm({ areas, onCreated }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [menus, setMenus] = useState(new Set(["dashboard"]));
  const [busy, setBusy] = useState(false);

  const toggle = (k) => {
    const next = new Set(menus);
    next.has(k) ? next.delete(k) : next.add(k);
    setMenus(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/roles", { key: key.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"), label: label.trim(), menus: [...menus] });
      toast.success(`Role "${data.label}" dibuat`);
      onCreated(data);
      setOpen(false);
      setKey(""); setLabel(""); setMenus(new Set(["dashboard"]));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="role-create-open-button" className="w-full bg-white rounded-3xl p-6 border-2 border-dashed border-slate-200 text-slate-500 font-bold flex items-center justify-center gap-2 hover:border-blue-300 hover:text-blue-600 transition">
        <Plus className="w-4 h-4" />Tambah Role Baru
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="bg-white rounded-3xl p-6 shadow-sm border border-blue-200 space-y-3" data-testid="role-create-form">
      <p className="font-extrabold text-slate-900">Role Baru</p>
      <input required className={inputCls} placeholder="Nama role (mis. Admin Youth)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="role-create-label-input" />
      <input required className={`${inputCls} font-mono text-sm`} placeholder="kode_role (huruf kecil, angka, underscore)" value={key} onChange={(e) => setKey(e.target.value)} data-testid="role-create-key-input" />
      <div className="grid grid-cols-2 gap-2">
        {areas.map((a) => (
          <label key={a.key} className="flex items-center gap-2 text-sm font-semibold text-slate-600 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={menus.has(a.key)} onChange={() => toggle(a.key)} />
            {a.label}
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <button disabled={busy} className={`${btnPrimary} flex-1 !py-2.5 text-sm`} data-testid="role-create-submit-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buat Role"}</button>
        <button type="button" onClick={() => setOpen(false)} className="px-4 text-sm font-bold text-slate-500">Batal</button>
      </div>
    </form>
  );
}

export default function Roles() {
  const [roles, setRoles] = useState(null);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    api.get("/admin/roles").then((r) => setRoles(r.data));
    api.get("/admin/menu-areas").then((r) => setAreas(r.data));
  }, []);

  if (!roles) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64" /></div>;

  return (
    <div data-testid="admin-roles-page">
      <AdminTitle eyebrow="Akses & Peran" title="Hak Akses">
        <Link to="/admin/invitations" data-testid="roles-goto-invitations" className="text-sm font-bold text-blue-600 self-center">Undang Pengurus →</Link>
      </AdminTitle>
      <p className="-mt-6 mb-6 text-sm text-slate-500 flex items-center gap-1.5"><Shield className="w-4 h-4 text-blue-600" />Atur menu apa saja yang bisa diakses tiap role, atau buat role baru khusus untuk kebutuhan tertentu.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {roles.map((r) => (
          <RoleCard key={r.key} role={r} areas={areas}
            onSaved={(updated) => setRoles((list) => list.map((x) => (x.key === updated.key ? updated : x)))}
            onDeleted={(key) => setRoles((list) => list.filter((x) => x.key !== key))} />
        ))}
        <NewRoleForm areas={areas} onCreated={(created) => setRoles((list) => [...list, created])} />
      </div>
    </div>
  );
}
